import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Plot from 'react-plotly.js';
import { selectRTFO } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { getSeismicEventColor } from './RTFOMapWithWellbore';
import { Typography, CircularProgress } from '@mui/material';

export const RTFOSeismicChart = ({ seismicData }) => {

  const theme = useTheme();
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const stageData = currentRTFO?.stageData;

  const darkMode = user?.value?.darkMode;

  const padStartTimestamp = stageData ? Math.min(...Object.keys(stageData)?.map(stageName => stageData?.[stageName]?.stageStartTimestamp)) : 0;

  const firstStageName = currentRTFO?.stages?.find(s => currentRTFO?.stageData?.[s]?.coldboreData);
  const lastStageName = currentRTFO?.stages?.findLast(s => currentRTFO?.stageData?.[s]?.coldboreData);

  const [seismicFilter, setSeismicFilter] = useState([]);

  const rangeRef = useRef(() => {
    if (currentRTFO?.stages && stageData) {
      return [stageData?.[firstStageName]?.stageStartTimestamp, stageData?.[lastStageName]?.stageStartTimestamp + 3 * 60 * 60 * 1000];
      // return [stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageStartTimestamp - 24 * 60 * 60 * 1000, stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageStartTimestamp]
      // return [new Date(stageData[Object.keys(stageData)?.[0]]?.coldboreData?.[0]?.timestamp)?.toLocaleDateString(), new Date(stageData[Object.keys(stageData)?.[0]]?.coldboreData?.[0]?.timestamp + 24 * 60 * 60 * 1000)?.toLocaleDateString()]
    } else {
      return [0, 0];
    };
  });

  const layout ={
    yaxis: { // Magnitude y axis
      linecolor: chartColors[4],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[4],
      title: {
        text: 'Local Magnitude (ML)',
        font: {
          size: 12
        },
      },
      range: [0, 5],
      rangemode: 'nonnegative'
    },
    yaxis2: { // Pressure y axis
      linecolor: chartColors[2],
      showgrid: false,
      color: chartColors[2],
      title: {
        text: 'Treating Pressure (MPa)',
        font: {
          size: 12
        },
      },
      side: 'right',
      position: 0,
      anchor: 'x',
      range: [0, 100],
      rangemode: 'nonnegative'
    },
    yaxis3: { // Rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: 'Slurry Rate (m3/min)',
        font: {
          size: 12
        },
      },
      side: 'right',
      anchor: 'free',
      overlaying: 'y2',
      position: 1,
      range: [0, 25],
      rangemode: 'nonnegative'
    },
    xaxis: { // Time x axis
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      type: 'date',
      domain: [0, 0.95],
      // showspikes: true,
      // spikethickness: 2,
      // spikedash: "dot",
      // spikecolor: "#999999",
      // spikemode: "across",
      range: stageData ? [stageData?.[firstStageName]?.stageStartTimestamp, stageData?.[lastStageName]?.stageStartTimestamp + 2 * 60 * 60 * 1000] : [0, 0],
      // range: stageData ? [stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageStartTimestamp - 24 * 60 * 60 * 1000, stageData?.[currentRTFO?.stages?.slice(-1)?.[0]]?.stageStartTimestamp] : [0, 0],
      // range: stageData ? [stageData[Object.keys(stageData)?.[0]]?.coldboreData?.[0]?.timestamp, stageData[Object.keys(stageData)?.[0]]?.coldboreData?.[0]?.timestamp + 24 * 60 * 60 * 1000] : [0, 0],
      rangeslider: { range: rangeRef.current }
    },
    hovermode: "closest",
    // hoverdistance: 5, // Distance to show hover label of data point
    // spikedistance: 1000,
    autosize: true,
    showlegend: false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey'
      },
      y: -0.1
    },
    shapes: [ 
      
    ],
    annotations: [
      
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 50},
    images: [
      {
        x: 0.035,
        y: 1.025,
        sizex: 0.05,
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "page",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };


  const data = useMemo(() => {
    try {
      // add traces
      let traces = [];

      if (stageData && !currentRTFO?.isLoadingData) {
        const stageNames = Object.keys(stageData);
        
        // add stages with pumping data
        for (const stageName of stageNames?.filter(s => stageData?.[s]?.coldboreData)) {
          // add pressure and rate traces
          let pumpingData = [];
          let XPressure = [];
          let YPressure = [];
          let XRate = [];
          let YRate = [];

          const stage = stageData[stageName];
          pumpingData = resampleTimeSeries(stage.coldboreData, stage.highResolutionData ? 30 : 3);
    
          pumpingData?.forEach(d => {
            XPressure.push(d.pressure > 0 ? d.timestamp : null);
            YPressure.push(d.pressure > 0 ? d.pressure : null);
            XRate.push(d.rate > 0 ? d.timestamp : null);
            YRate.push(d.rate > 0 ? d.rate : null);
          });
    
          traces.push(
            { // pressure trace
              x: XPressure,
              y: YPressure,
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              yaxis: 'y2',
              name: 'Pressure (MPa)',
              line: {
                width: 2,
                color: chartColors[2],
                opacity: 0.9,
              },
              connectgaps: false,
              hovertemplate: `Stage: ${stageName?.split('_')?.slice(1,)?.join(' ')} <br>` + 
                            'Date: %{x} <br>' +
                            'Pressure: %{y:.2f} MPa'
            }
          );
    
          traces.push(
            { // rate trace
              x: XRate,
              y: YRate,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y3',
              name: 'Rate (m3/min)',
              line: {
                width: 2,
                color: chartColors[3],
                opacity: 0.9,
              },
              connectgaps: false,
              hovertemplate: `Stage: ${stageName?.split('_')?.slice(1,)?.join(' ')} <br>` +
                            'Date: %{x} <br>' +
                            'Rate: %{y:.2f} m3/min'
            }
          );
        };

        for (const stageName of stageNames?.filter(s => !stageData?.[s]?.coldboreData)) {
          const stageStartTimestamp = stageData?.[stageName]?.stageStartTimestamp;
          const stageEndTimestamp = stageData?.[stageName]?.stageEndTimestamp || (stageData?.[stageName]?.stageStartTimestamp + stageData?.[stageName]?.metrics?.stageTime * 60 * 1000);
          const backgroundX = [stageStartTimestamp, stageStartTimestamp + 100, stageEndTimestamp, stageEndTimestamp + 100];
          const backgroundYPressure = [0, (stageData?.[stageName]?.metrics?.averagePumpingPressure || 0), (stageData?.[stageName]?.metrics?.averagePumpingPressure || 0), 0];
          const backgroundYRate = [0, (stageData?.[stageName]?.metrics?.averagePumpingRate || 0), (stageData?.[stageName]?.metrics?.averagePumpingRate || 0), 0];
          
          traces.push(
            { // background trace
              x: backgroundX,
              y: backgroundYPressure,
              mode: 'line',
              type: 'scatter',
              xaxis: 'x1',
              yaxis: 'y2',
              name: 'Pressure (MPa)',
              line: {
                width: 1,
                color: chartColors[2],
                opacity: 0.75,
                dash: 'dot'
              },
              marker: {
                color: 'transparent'
              },
              connectgaps: true,
              hovertemplate: `Stage: ${stageName?.split('_')?.slice(1,)?.join(' ')} <br>` + 
                            'Date: %{x} <br>' +
                            'Pressure: %{y:.2f} MPa'
            }
          );

          traces.push(
            { // background trace
              x: backgroundX,
              y: backgroundYRate,
              mode: 'line',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y3',
              name: 'Rate (m3/min)',
              line: {
                width: 1,
                color: chartColors[3],
                opacity: 0.75,
                dash: 'dot'
              },
              marker: {
                color: 'transparent'
              },
              connectgaps: true,
              hovertemplate: `Stage: ${stageName?.split('_')?.slice(1,)?.join(' ')} <br>` + 
                            'Date: %{x} <br>' +
                            'Rate: %{y:.2f} m3/min'
            }
          );
        };

      };


      let X = [];
      let Y = [];
      seismicData?.forEach(event => {
        const isEventFilteredOut = determineIsEventFilteredOut(seismicFilter, event?.localMagnitude);

        if (event.time > padStartTimestamp && !isEventFilteredOut) {
          X.push(event?.time);
          Y.push(event?.localMagnitude);
        };
      });

      traces.push(
        { // seismic event
          x: X,
          y: Y,
          mode: 'markers',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Seismic event',
          marker: {
            size: 10,
            color: Y.map(val => getSeismicEventColor(val)),
            opacity: 1,
          },
          // text: Y?.map(d => d),
          // hovertext: Y?.map(d => `Local Magnitude: ${d}`),
          // hoverinfo: 'text',
          hovertemplate: 'Date: %{x}' +
                        'Local Magnitude: %{y}'
        }
      );

      return traces;

    } catch (error) {
      console.log(error);
      return [];
    };
  }, [stageData, currentRTFO?.isLoadingData, seismicData, seismicFilter, padStartTimestamp]);

  useEffect(() => {
    rangeRef.current = layout.xaxis.rangeslider.range;
  }, [layout.xaxis?.rangeslider?.range])

  

  if (stageData) {
    const stageNames = Object.keys(stageData);

    for (let stageName of stageNames) {
      const stage = stageData[stageName];

      if (!stage) continue;

      if (stage?.coldboreData) {
        layout.shapes.push(
          { // stage rect
            type: 'rect',
            yref: 'paper',
            xref: 'x',
            x0: stage?.coldboreData?.[0]?.timestamp,
            y0: 0,
            x1: stage?.coldboreData?.slice(-1)?.[0]?.timestamp,
            y1: 1,
            opacity: 0.1,
            fillcolor: stageName === currentRTFO?.currentStageName ? chartColors[1] : chartColors[0],
            line: {
              width: 0
            }
          }
        );
        layout.annotations.push(
          {
            text: stageName?.split('_')?.slice(1,)?.join(' '),
              font: {
              size: 10,
              color: stageName === currentRTFO?.currentStageName ? chartColors[1] : chartColors[0],
              weight: 'bold'
            },
            textangle: '-90',
            showarrow: false,
            align: 'center',
            x: (stage?.coldboreData?.[0]?.timestamp + stage?.coldboreData?.slice(-1)?.[0]?.timestamp) / 2, //position in x domain
            y: 0.99, //position in y domain
            xref: 'x',
            yref: 'paper',
            ay: -40,
            ax: -40
          },
        );
      } else {
        layout.shapes.push(
          { // stage line
            type: 'rect',
            yref: 'paper',
            xref: 'x',
            x0: stage?.stageStartTimestamp,
            y0: 0,
            x1: stage?.stageStartTimestamp + stage?.metrics?.stageTime * 60 * 1000,
            y1: 1,
            opacity: 0.1,
            fillcolor: stageName === currentRTFO?.currentStageName ? chartColors[1] : chartColors[0],
            line: {
              width: 2,
              color: stageName === currentRTFO?.currentStageName ? chartColors[1] : chartColors[0]
            }
          }
        );
        layout.annotations.push(
          {
            text: stageName?.split('_')?.slice(1,)?.join(' '),
              font: {
              size: 10,
              color: stageName === currentRTFO?.currentStageName ? chartColors[1] : chartColors[0],
              weight: 'bold'
            },
            // bgcolor: (darkMode ? '#37474f' : '#fff'),
            textangle: '-90',
            showarrow: false,
            align: 'center',
            x: (stage?.stageStartTimestamp + stage?.stageStartTimestamp + stage?.metrics?.stageTime * 60 * 1000) / 2, //position in x domain
            y: 0.99, //position in y domain
            xref: 'x',
            yref: 'paper',
            ay: -40,
            ax: -40
          },
        );
      }
  

    };
  };

  for (const event of seismicData) {

    const isEventFilteredOut = determineIsEventFilteredOut(seismicFilter, event?.localMagnitude);

    if (event?.time > padStartTimestamp && !isEventFilteredOut) {
      layout.shapes.push({
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.time),
        y0: 0,
        x1: new Date(event?.time),
        y1: 1,
        line: {
          color: getSeismicEventColor(event?.localMagnitude),
          width: 1,
          dash: 'dot',
          opacity: 0.9
        },
      })
    };
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-Seismic-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };

  const handleLegendItemClick = (seismicValue) => {
    return seismicFilter.includes(seismicValue) ? setSeismicFilter(prev => prev.filter(d => d !== seismicValue)) : setSeismicFilter(prev => [...prev, seismicValue]);
  };

  try {
    return (
      currentRTFO?.isLoadingData ? 
      <div>
        <CircularProgress
          size={24}
        />
      </div> :
      <div style={{position: 'relative', width: '100%', height: '100%'}}>
        <Plot
          data={data}
          layout={layout}
          config={config}
          useResizeHandler={true}
          style={{width: '100%', height: '100%'}}
        />

        <div style={{position: 'absolute', width: 800, left: '50%', transform: 'translateX(-400px)', top: 30}}>
          <div style={{display: 'flex', flexDirection: 'row'}}>
            {[0.75, 1.5, 2.5, 3.5, 4.5].map(seismicValue => {
              return (
              <span 
                style={{cursor: 'pointer', opacity: seismicFilter.includes(seismicValue) ? 0.25 : 1, display: 'flex', flexDirection: 'row', alignItems: 'center', marginRight: 20}}
                onClick={() => handleLegendItemClick(seismicValue)}
                key={seismicValue}
              >
                <div style={{height: 10, width: 10, borderRadius: '50%', backgroundColor: getSeismicEventColor(seismicValue), marginRight: 5}}></div>
                <Typography fontSize='12px'>{
                  seismicValue < 1 ? 'Local Magnitude < 1' :
                  seismicValue < 2 ? 'Local Magnitude 1 - 2' :
                  seismicValue < 3 ? 'Local Magnitude 2 - 3' :
                  seismicValue < 4 ? 'Local Magnitude 3 - 4' :
                  seismicValue < 5 ? 'Local Magnitude > 4' : ''
                }</Typography>
              </span>
              )
            })}
            
          </div>
        </div>
      </div>
    );
    
  } catch (error) {
    return <></>;
  };

};

export function determineIsEventFilteredOut(seismicFilter, seismicValue) {
  let isEventFilteredOut = false;

  if (seismicFilter?.length > 0) {
    if (seismicFilter?.includes(0.75) && seismicValue < 1) {
      isEventFilteredOut = true;
    } else if (seismicFilter?.includes(1.5) && seismicValue >= 1 && seismicValue < 2) {
      isEventFilteredOut = true;
    } else if (seismicFilter?.includes(2.5) && seismicValue >= 2 && seismicValue < 3) {
      isEventFilteredOut = true;
    } else if (seismicFilter?.includes(3.5) && seismicValue >= 3 && seismicValue < 4) {
      isEventFilteredOut = true;
    } else if (seismicFilter?.includes(4.5) && seismicValue >= 4) {
      isEventFilteredOut = true;
    }


  };

  return isEventFilteredOut;
};

function resampleTimeSeries(data, targetResolution) {
  try {

    if (!data) return [];

    // Calculate the number of data points in the target resolution
    const targetDataPoints = Math.floor(data.length / targetResolution);
  
    // Create a new array to store the resampled data
    const resampledData = [];
  
    for (let i = 0; i < targetDataPoints; i++) {
      const startIndex = i * targetResolution;
      const endIndex = startIndex + targetResolution;
  
      // Extract the subset of data for the current target resolution
      const subset = data.slice(startIndex, endIndex);
  
      // Calculate the average values for the subset
      const averagePressure = subset.reduce((sum, element) => sum + element.pressure, 0) / targetResolution;
      const averageRate = subset.reduce((sum, element) => sum + element.rate, 0) / targetResolution;
  
      // Calculate the timestamp for the resampled data
      const timestamp = subset[Math.floor(targetResolution / 2)].timestamp;
  
      // Create a new resampled data point
      const resampledPoint = {
        timestamp,
        pressure: averagePressure,
        rate: averageRate
      };
  
      // Push the resampled data point to the resampledData array
      resampledData.push(resampledPoint);
    };
  
    return resampledData;

  } catch(error) {
    console.log(error);
    return [];
  };
};